import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from './supabase';
import { 
  FiUpload, 
  FiFileText, 
  FiDownload, 
  FiTrash2,
  FiCheckCircle
} from "react-icons/fi";
const CONFIG = require("./config");

function Overview() {
  const { currentUser } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [quota, setQuota] = useState(null);
  const [recentFiles, setRecentFiles] = useState([]);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [polling, setPolling] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const intervalRef = useRef(null);

  const fetchHistory = async () => {
    try {
      // Get the Supabase session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error("No active session");
        return;
      }
      
      const token = session.access_token;
      console.log("Fetching history with token:", token ? "token present" : "no token");
      
      const response = await fetch(CONFIG.API_URL+"/history", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch history");
      }
      
      const data = await response.json();
      setRecentFiles(data);
    } catch (error) {
      console.error("Error fetching transcription history:", error);
    }
  };

  // =============== FETCH QUOTA ===============
  const fetchQuota = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session.access_token;
      const response = await fetch(CONFIG.API_URL+"/quota", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch quota");
      }
      const data = await response.json();
      setQuota(data);
    } catch (error) {
      console.error("Error fetching quota:", error);
    }
  };

  // =============== AUTH & INITIAL DATA LOAD ===============
  useEffect(() => {
    if (currentUser) {
      fetchHistory();
      fetchQuota();
    }
  }, [currentUser]);

  // =============== CHECK PREMIUM STATUS ===============
  useEffect(() => {
    const checkPremiumStatus = async () => {
      if (currentUser) {
        try {
          // Call the Supabase RPC function to check premium status
          const { data, error } = await supabase.rpc('check_premium_status');
          
          if (error) throw error;
          setIsPremium(data || false);
        } catch (err) {
          console.error("Error checking premium status:", err);
          setIsPremium(false);
        }
      } else {
        setIsPremium(false);
      }
    };
    
    checkPremiumStatus();
  }, [currentUser]);

  // =============== FETCH QUOTA IF FREE ===============
  useEffect(() => {
    if (currentUser) {
      if (isPremium) {
        // Premium users have no daily limit
        setQuota({ used: 0, remaining: null, limit: "unlimited" });
      } else {
        fetchQuota();
      }
    }
  }, [currentUser, isPremium]);

  // =============== POLLING LOGIC ===============
  useEffect(() => {
    if (polling && intervalRef.current === null) {
      intervalRef.current = setInterval(() => {
        fetchHistory();
      }, 10000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [polling]);

  useEffect(() => {
    if (recentFiles.length > 0 && !recentFiles.some((f) => f.status === "transcribing" || f.status === "processing")) {
      setPolling(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [recentFiles]);

  const handlePremiumSubscription = async () => {
    try {
      // Get the session token
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        // Try to refresh session if not available
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (!refreshData.session) {
          throw new Error("No active session");
        }
      }
      
      const token = data.session.access_token;
      
      console.log("Starting premium subscription process...");
      
      const response = await fetch(
        CONFIG.CREATE_CHECKOUT_SESSION,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          // No need to send user ID - it will be extracted from the token
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Subscription error");
      }
      
      const checkoutData = await response.json();
      window.location.href = checkoutData.url;
    } catch (error) {
      console.error("Premium subscription error:", error);
      setModalTitle("Premium Subscription Error");
      setModalContent("Subscription failed: " + error.message);
      setShowModal(true);
    }
  };

  const uploadFile = async (file) => {
    try {
      // Get the Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }
      
      // Use the access token
      const token = session.access_token;
      console.log("Got token:", token ? "yes" : "no");
      
      const formData = new FormData();
      formData.append("file", file);
  
      const response = await fetch(CONFIG.API_URL+"/transcribe", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Upload error");
      }
  
      const data = await response.json();
      fetchHistory();
      if (data.status === "transcribing" || data.status === "processing") {
        setPolling(true);
      }
      setToastMessage("File uploaded successfully! Transcription in progress...");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 5000);
    } catch (error) {
      console.error("Upload failed:", error);
      setModalTitle("Upload Error");
      setModalContent("Upload failed: " + error.message);
      setShowModal(true);
    }
  };

  const deleteFile = async (recordId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session.access_token;
      const response = await fetch(CONFIG.API_URL+`/delete/${recordId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Deletion error");
      }
      fetchHistory();
      setToastMessage("File deleted successfully!");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    } catch (error) {
      console.error("Deletion failed:", error);
      setModalTitle("Deletion Error");
      setModalContent("Deletion failed: " + error.message);
      setShowModal(true);
    }
  };

  // =============== DRAG & DROP AND FILE INPUT ===============
  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleClickUpload = () => {
    document.getElementById("fileInput").click();
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  };

  // =============== FILE DETAILS (MODAL) ===============
  const handleFileClick = async (recordId, filename) => {
    try {
      console.log("Fetching file details for:", recordId, filename);
      
      // Set modal visible right away with a loading state
      setModalTitle(filename);
      setModalContent("Loading transcription...");
      setShowModal(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session.access_token;
      console.log("Token generated successfully:", !!token);
      
      // Make sure API_URL is properly defined
      if (!CONFIG.API_URL) {
        throw new Error("API URL is not defined in configuration");
      }
      
      const response = await fetch(`${CONFIG.API_URL}/result/${recordId}`, {
        method: "GET",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file details: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Received data:", data);
      
      // Use the new field name from our updated endpoint
      // Also add a fallback to the old field name for backward compatibility
      const transcriptionText = data.transcription_text || data.transcription_result || "No transcription available";
      
      // Format the content more nicely for display
      let formattedContent = `${transcriptionText}`;
      
      // Add language information if available
      if (data.detected_language) {
        formattedContent = `Detected Language: ${data.detected_language}\n\n${formattedContent}`;
      }
      
      setModalContent(formattedContent);
      
    } catch (error) {
      console.error("Error fetching file details:", error);
      setModalTitle("Error");
      setModalContent(`Error fetching file details: ${error.message}`);
      // No need to set showModal here since we already did it at the beginning
    }
  };

  // =============== COPY TO CLIPBOARD & DOWNLOAD FUNCTIONS ===============
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(modalContent)
      .then(() => {
        setToastMessage("Copied to clipboard!");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      })
      .catch((err) => console.error("Failed to copy:", err));
  };

  const downloadTranscript = () => {
    const element = document.createElement("a");
    const file = new Blob([modalContent], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = `${modalTitle.replace(/\.[^/.]+$/, "")}_transcript.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // =============== PROGRESS BAR CALC ===============
  let progressPercent = 0;
  if (quota && quota.limit !== "unlimited") {
    progressPercent = (quota.used / quota.limit) * 100;
    progressPercent = progressPercent > 100 ? 100 : progressPercent; // cap at 100
  }

  if (!currentUser) {
    return (
      <div className="login-prompt">
        <h2>Please log in to access your dashboard</h2>
        <p>You need to be logged in to view and manage your transcriptions.</p>
        <a href="https://apptest.instanttranscriber.com/login" className="primary-button">Log In</a>
      </div>
    );
  }

  return (
    <>
      {/* =============== WELCOME SECTION =============== */}
      <section className="welcome-section">
        <h1>Welcome to Your Transcription Dashboard</h1>
        <p>Upload audio files and get accurate transcriptions in minutes.</p>
      </section>

      {/* =============== QUOTA & PREMIUM SECTION =============== */}
      <section className="quota-section">
        {!isPremium ? (
          <div className="quota-card free-plan">
            <div className="quota-info">
              <h2>Free Plan</h2>
              <p>You have used {quota?.used || 0} of {quota?.limit || 3} daily transcriptions</p>
              
              <div className="progress-container">
                <div 
                  className="progress-bar" 
                  style={{ width: `${progressPercent}%`, backgroundColor: progressPercent > 80 ? '#e63946' : '#4f46e5' }}
                ></div>
              </div>
              
              <ul className="plan-limits">
                <li><FiCheckCircle /> 3 transcriptions per day</li>
                <li><FiCheckCircle /> Max 10 minutes per file</li>
                <li><FiCheckCircle /> Max 15 MB per file</li>
              </ul>
            </div>
            
            <div className="upgrade-container">
              <button className="upgrade-button" onClick={handlePremiumSubscription}>
                Upgrade to Premium
              </button>
              <p className="upgrade-benefits">Get unlimited transcriptions, longer files, and priority support</p>
            </div>
          </div>
        ) : (
          <div className="quota-card premium-plan">
            <div className="premium-badge">Premium</div>
            <div className="quota-info">
              <h2>Premium Plan</h2>
              <p>Enjoy unlimited transcriptions with premium features</p>
              
              <ul className="plan-limits">
                <li><FiCheckCircle /> Unlimited transcriptions</li>
                <li><FiCheckCircle /> Max 10 hours per file</li>
                <li><FiCheckCircle /> Max 5 GB per file</li>
                <li><FiCheckCircle /> Priority support</li>
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* =============== UPLOAD SECTION =============== */}
      <section className="upload-section">
        <h2>Upload Audio</h2>
        <div 
          className={`upload-area ${isDragging ? 'dragging' : ''}`}
          onClick={handleClickUpload}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <FiUpload className="upload-icon" />
          <p>Drag and drop your audio file here or click to browse</p>
          <p className="upload-formats">Supports .mp3, .m4a, .wav, .flac, .ogg, .aiff</p>
          <input
            type="file"
            id="fileInput"
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="audio/*"
          />
        </div>
      </section>

      {/* =============== RECENT FILES SECTION =============== */}
      <section className="files-section">
        <h2>Recent Transcriptions</h2>
        
        {recentFiles.length === 0 ? (
          <div className="no-files-message">
            <p>You haven't uploaded any files yet. Upload your first audio file to get started!</p>
          </div>
        ) : (
          <div className="files-list">
            
            {recentFiles.map((file) => (
  <div 
    key={file.id} 
    className={`file-card ${file.status === 'completed' ? 'completed' : 'processing'}`}
  >
    <div 
      className="file-info"
      onClick={(e) => {
        // Stop propagation to prevent conflicts with other click handlers
        e.stopPropagation();
        if (file.status.trim().toLowerCase() === 'completed') {
          handleFileClick(file.id, file.original_filename);
        }
      }}
      style={{ cursor: file.status === 'completed' ? 'pointer' : 'default' }}
    >
      <div className="file-header">
        <span className="file-name">{file.original_filename}</span>
        <span className={`file-status status-${file.status}`}>{file.status}</span>
      </div>
      <div className="file-meta">
        <span>Uploaded: {file.uploaded_time}</span>
        {file.duration && <span>Duration: {file.duration}</span>}
      </div>
    </div>
    
    <div className="file-actions">
      {file.status === 'completed' && (
        <button 
          className="icon-button view-button" 
          onClick={(e) => {
            e.stopPropagation();
            console.log(`View button clicked for file ${file.id}`);
            handleFileClick(file.id, file.original_filename);
          }}
          title="View Transcription"
        >
          <FiFileText />
        </button>
      )}
      <button 
        className="icon-button delete-button" 
        onClick={(e) => {
          e.stopPropagation();
          deleteFile(file.id);
        }}
        title="Delete File"
      >
        <FiTrash2 />
      </button>
    </div>
  </div>
))}

          </div>
        )}
      </section>

      {/* =============== MODAL =============== */}
      {showModal && (
  <div className="modal-overlay" onClick={() => setShowModal(false)}>
    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>{modalTitle}</h3>
        <button 
          className="icon-button" 
          onClick={() => setShowModal(false)}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      
      <div className="modal-content">
        <pre>{modalContent}</pre>
      </div>
      
      <div className="modal-footer">
        <button className="secondary-button" onClick={() => setShowModal(false)}>
          Close
        </button>
        {!modalTitle.includes("Error") && (
          <>
            <button className="primary-button" onClick={copyToClipboard}>
              <FiFileText /> Copy Text
            </button>
            <button className="primary-button" onClick={downloadTranscript}>
              <FiDownload /> Download
            </button>
          </>
        )}
      </div>
    </div>
  </div>
)}

      {/* =============== TOAST =============== */}
      {showToast && (
        <div className="toast-message">
          <FiCheckCircle />
          <span>{toastMessage}</span>
        </div>
      )}
    </>
  );
}

export default Overview;